import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { Clock, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { MysteryBox } from "./MysteryBox";
import { RewardModal } from "./RewardModal";
import {
  BOX_COUNT,
  BoxVisualState,
  GamePhase,
  MysteryBoxGameProps,
  Reward,
} from "./types";
import {
  generateOtherBoxRewards,
  generateWeightedReward,
  trackAnalyticsEvent,
  triggerConfettiPlaceholder,
  triggerSoundPlaceholder,
} from "./utils";

const OPEN_SEQUENCE_MS = {
  selected: 400,
  opening: 1400,
  revealed: 900,
  modal: 400,
};

export function MysteryBoxGame({
  className,
  dailyAttemptsRemaining = 3,
  onRewardClaimed,
}: MysteryBoxGameProps) {
  const [phase, setPhase] = useState<GamePhase>("idle");
  const [selectedBox, setSelectedBox] = useState<number | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const [reward, setReward] = useState<Reward | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [otherRewards, setOtherRewards] = useState<Record<number, Reward>>({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSize, setConfettiSize] = useState({ width: 0, height: 0 });
  const [attemptsLeft, setAttemptsLeft] = useState(dailyAttemptsRemaining);

  useEffect(() => {
    const update = () =>
      setConfettiSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const resetGame = useCallback(() => {
    setPhase("idle");
    setSelectedBox(null);
    setIsOpening(false);
    setReward(null);
    setIsModalOpen(false);
    setGameCompleted(false);
    setOtherRewards({});
    setShowConfetti(false);
    setAttemptsLeft(dailyAttemptsRemaining);
  }, [dailyAttemptsRemaining]);

  const handlePlayAgain = useCallback(() => {
    if (attemptsLeft <= 0) return;
    setIsModalOpen(false);
    resetGame();
  }, [attemptsLeft, resetGame]);

  const getBoxState = useCallback(
    (boxId: number): BoxVisualState => {
      if (gameCompleted && otherRewards[boxId]) return "revealed";
      if (phase === "idle") return "closed";
      if (selectedBox === boxId) {
        if (phase === "revealed" || phase === "complete") return "revealed";
        if (phase === "opening" || isOpening) return "opening";
        return "selected";
      }
      if (phase !== "idle") return "locked";
      return "closed";
    },
    [phase, selectedBox, isOpening, gameCompleted, otherRewards]
  );

  const handleSelectBox = useCallback(
    (boxId: number) => {
      if (phase !== "idle" || attemptsLeft <= 0) return;

      setAttemptsLeft((a) => Math.max(0, a - 1));
      const wonReward = generateWeightedReward();
      setSelectedBox(boxId);
      setReward(wonReward);
      setPhase("selected");
      trackAnalyticsEvent("mystery_box_selected", { boxId });
      triggerSoundPlaceholder("box_select");

      const t1 = window.setTimeout(() => {
        setPhase("opening");
        setIsOpening(true);
        triggerSoundPlaceholder("box_open");
      }, OPEN_SEQUENCE_MS.selected);

      const t2 = window.setTimeout(() => {
        setPhase("revealed");
        setIsOpening(false);
        triggerConfettiPlaceholder();
        setShowConfetti(true);
        trackAnalyticsEvent("mystery_box_reward", { reward: wonReward });
        onRewardClaimed?.(wonReward);
      }, OPEN_SEQUENCE_MS.selected + OPEN_SEQUENCE_MS.opening);

      window.setTimeout(() => {
        setPhase("complete");
        setGameCompleted(true);
        setIsModalOpen(true);
        const others = generateOtherBoxRewards(wonReward, BOX_COUNT - 1);
        const map: Record<number, Reward> = {};
        let idx = 0;
        for (let i = 1; i <= BOX_COUNT; i++) {
          if (i !== boxId) {
            map[i] = others[idx++];
          }
        }
        setOtherRewards(map);
      }, OPEN_SEQUENCE_MS.selected + OPEN_SEQUENCE_MS.opening + OPEN_SEQUENCE_MS.revealed);
    },
    [phase, attemptsLeft, onRewardClaimed]
  );

  const isCentered =
    phase !== "idle" &&
    phase !== "complete" &&
    selectedBox !== null;
  const showOtherReveals = gameCompleted;

  const boxIds = useMemo(
    () => Array.from({ length: BOX_COUNT }, (_, i) => i + 1),
    []
  );

  return (
    <>
      {showConfetti && confettiSize.width > 0 && (
        <Confetti
          width={confettiSize.width}
          height={confettiSize.height}
          numberOfPieces={280}
          recycle={false}
          colors={["#fbbf24", "#a855f7", "#3b82f6", "#fde68a", "#c084fc"]}
        />
      )}

      <Card
        className={cn(
          "relative overflow-hidden border border-white/10",
          "bg-gradient-to-br from-[#0a0f1a]/90 via-[#0f172a]/80 to-[#1e1b4b]/90",
          "shadow-[0_0_60px_rgba(99,102,241,0.15)] backdrop-blur-xl",
          className
        )}
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-48 w-48 rounded-full bg-purple-600/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-blue-600/15 blur-3xl" />

        <CardContent className="relative p-4 sm:p-8">
          <div className="mb-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="flex items-center gap-2 text-center sm:text-left">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <div>
                <h2 className="text-lg font-bold text-white sm:text-xl">
                  Mystery Box
                </h2>
                <p className="text-xs text-purple-200/70 sm:text-sm">
                  Pick one box — win points
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-purple-200/60">
              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 font-medium text-amber-200">
                {attemptsLeft} attempts left
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Cooldown: —
              </span>
            </div>
          </div>

          <motion.div
            layout
            className={cn(
              "mx-auto grid max-w-2xl gap-4 sm:gap-6",
              isCentered
                ? "grid-cols-1 place-items-center"
                : "grid-cols-2 sm:grid-cols-3"
            )}
            role="group"
            aria-label="Mystery boxes. Choose one to open."
          >
            <AnimatePresence mode="popLayout">
              {boxIds.map((id) => {
                const hidden =
                  isCentered && selectedBox !== null && id !== selectedBox && !showOtherReveals;
                if (hidden) return null;

                const boxReward =
                  selectedBox === id
                    ? reward
                    : otherRewards[id] ?? null;

                return (
                  <MysteryBox
                    key={id}
                    id={id}
                    state={getBoxState(id)}
                    isSelected={selectedBox === id}
                    isCentered={isCentered && selectedBox === id}
                    reward={boxReward}
                    disabled={phase !== "idle" || attemptsLeft <= 0}
                    onSelect={handleSelectBox}
                    ariaLabel={`Mystery box ${id}${selectedBox === id ? ", selected" : ""}`}
                  />
                );
              })}
            </AnimatePresence>
          </motion.div>

          {showOtherReveals && (
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 text-center text-xs text-purple-200/50"
            >
              Other boxes have been revealed — see what you could have won!
            </motion.p>
          )}

          {gameCompleted && !isModalOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 flex justify-center"
            >
              <Button
                type="button"
                onClick={resetGame}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-500 hover:to-purple-600"
              >
                <RotateCcw className="h-4 w-4" />
                Reset Game
              </Button>
            </motion.div>
          )}

          {phase === "idle" && attemptsLeft <= 0 && (
            <p className="mt-4 text-center text-sm text-amber-200/80">
              No attempts remaining today. Come back tomorrow!
            </p>
          )}
        </CardContent>
      </Card>

      <RewardModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        reward={reward}
        onPlayAgain={handlePlayAgain}
      />
    </>
  );
}
